import React from 'react';
import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';

import styles from './Pagination.module.scss';

const PAGE_TRANSITION = {
  type: 'spring',
  stiffness: 500,
  damping: 35,
  mass: 0.5,
};

// Classic ellipsis truncation: first/last page, a window around the
// current page, and '...' placeholders for the gaps — keeps the bar a
// fixed width instead of rendering a button per page (which overflowed
// the page with large datasets, e.g. hundreds of employee pages).
const SIBLING_COUNT = 1;

const getPageItems = (currentPage, totalPages) => {
  const items = [];
  let lastPage;
  for (let page = 1; page <= totalPages; page += 1) {
    const isEdge = page === 1 || page === totalPages;
    const isNearCurrent =
      page >= currentPage - SIBLING_COUNT &&
      page <= currentPage + SIBLING_COUNT;
    if (isEdge || isNearCurrent) {
      if (lastPage && page - lastPage === 2) {
        items.push(lastPage + 1);
      } else if (lastPage && page - lastPage > 2) {
        items.push(`ellipsis-${lastPage}`);
      }
      items.push(page);
      lastPage = page;
    }
  }
  return items;
};

/**
 * Simple bordered-square pager: prev arrow, page numbers, next arrow.
 * Optionally paired with a rows-per-page select — pass `pageSize` +
 * `pageSizeOptions` + `onPageSizeChange` to opt in (used by Tasks); callers
 * that omit them get the exact same output as before this option existed.
 * Renders nothing when there's only one page and no page-size control —
 * pagination controls should only appear once there's something to do.
 */
export const Pagination = ({
  currentPage,
  totalPages,
  onPageChange,
  pageSize,
  pageSizeOptions,
  onPageSizeChange,
}) => {
  const showPageSize = Boolean(pageSizeOptions?.length && onPageSizeChange);
  const showPager = totalPages > 1;
  if (!showPager && !showPageSize) return null;

  const pages = showPager ? getPageItems(currentPage, totalPages) : [];

  return (
    <div className={styles.paginationBar}>
      {showPageSize && (
        <select
          className={styles.pageSizeSelect}
          value={pageSize}
          onChange={(e) => onPageSizeChange(Number(e.target.value))}
          aria-label="Rows per page"
        >
          {pageSizeOptions.map((size) => (
            <option key={size} value={size}>
              {size} per page
            </option>
          ))}
        </select>
      )}
      {showPager && (
        <div className={styles.pagination}>
          <button
            type="button"
            className={styles.navButton}
            disabled={currentPage <= 1}
            onClick={() => onPageChange(currentPage - 1)}
            aria-label="Previous page"
          >
            <FiChevronLeft size={16} />
          </button>
          <AnimatePresence mode="popLayout" initial={false}>
            {pages.map((page) =>
              typeof page === 'number' ? (
                <motion.button
                  key={page}
                  layout
                  initial={{ opacity: 0, scale: 0.6 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.6 }}
                  transition={PAGE_TRANSITION}
                  type="button"
                  className={classNames(styles.pageButton, {
                    [styles.pageButton_active]: page === currentPage,
                  })}
                  onClick={() => onPageChange(page)}
                >
                  {page === currentPage && (
                    <motion.span
                      layoutId="paginationActivePill"
                      className={styles.activePill}
                      transition={PAGE_TRANSITION}
                    />
                  )}
                  <span className={styles.pageButtonLabel}>{page}</span>
                </motion.button>
              ) : (
                <motion.span
                  key={page}
                  layout
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={PAGE_TRANSITION}
                  className={styles.ellipsis}
                  aria-hidden="true"
                >
                  &hellip;
                </motion.span>
              )
            )}
          </AnimatePresence>
          <button
            type="button"
            className={styles.navButton}
            disabled={currentPage >= totalPages}
            onClick={() => onPageChange(currentPage + 1)}
            aria-label="Next page"
          >
            <FiChevronRight size={16} />
          </button>
        </div>
      )}
    </div>
  );
};

Pagination.displayName = 'Pagination';
